import { Box, Button, InputAdornment, Menu, MenuItem } from '@mui/material';
import { SxProps } from '@mui/system';
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDown } from '@/icons/chevron-down';
import { Search as SearchIcon } from '@/icons/search';
import { InputField } from './input-field';
import { Scrollbar } from './scrollbar';

export interface SearchableSelectOption<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface Props<T extends string | number> {
  options: SearchableSelectOption<T>[];
  value: T;
  onChange: (value: T, option: SearchableSelectOption<T>) => void;
  hideSearch?: boolean;
  noOptionsText?: string;
  searchPlaceholder?: string;
  fullWidth?: boolean;
  sx?: SxProps;
}

export function SearchableSelect<T extends string | number>({
  options,
  value,
  onChange,
  hideSearch,
  noOptionsText,
  searchPlaceholder,
  fullWidth,
  sx,
}: Props<T>) {
  const { t } = useTranslation(['common']);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedOption = useMemo(
    () => options.find(option => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    if (hideSearch) {
      return options;
    }

    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return options;
    }

    return options.filter(option => option.label.toLowerCase().includes(normalizedQuery));
  }, [hideSearch, options, query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    window.setTimeout(() => {
      if (hideSearch) {
        firstMenuItemRef.current?.focus();
      } else {
        searchInputRef.current?.focus();
      }
    });
  }, [hideSearch, open]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
  };

  const handleOptionClick = (option: SearchableSelectOption<T>) => {
    if (option.disabled) {
      return;
    }

    onChange(option.value, option);
    handleClose();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();

      window.setTimeout(() => {
        firstMenuItemRef.current?.focus();
      });

      return;
    }

    if (event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  return (
    <>
      <Button
        ref={anchorRef}
        fullWidth={fullWidth}
        variant="outlined"
        color="inherit"
        onClick={handleOpen}
        endIcon={
          <ChevronDown
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: theme => theme.transitions.create('transform'),
            }}
          />
        }
        sx={{
          borderColor: 'neutral.300',
          color: 'text.primary',
          justifyContent: 'space-between',
          overflowWrap: 'anywhere',
          textAlign: 'left',
          whiteSpace: 'normal',
          '& .MuiButton-endIcon': {
            flexShrink: 0,
          },
          '&:hover': {
            borderColor: 'neutral.300',
          },
          ...sx,
        }}>
        {selectedOption?.label ?? ''}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        MenuListProps={{
          sx: {
            pb: 0.5,
            pt: hideSearch ? 0.5 : 0,
          },
        }}
        PaperProps={{
          sx: {
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: '60vh',
            maxWidth: 'calc(100vw - 32px)',
            minWidth: anchorRef.current?.clientWidth,
            mt: 0,
            overflow: 'hidden',
          },
        }}>
        {!hideSearch && (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              mb: 1,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
            onFocus={() => {
              searchInputRef.current?.focus();
            }}>
            <InputField
              autoFocus
              autoComplete="off"
              fullWidth
              inputRef={searchInputRef}
              onChange={handleSearchChange}
              onClick={event => event.stopPropagation()}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder ?? t('common:search')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{
                        mt: '0 !important',
                      }}>
                      <SearchIcon
                        sx={{
                          color: 'text.secondary',
                          fontSize: 20,
                          height: 20,
                          width: 20,
                        }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                minWidth: '100%',
                width: 'min-content',
                '& .MuiFilledInput-root': {
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  alignItems: 'center',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'background.paper',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'background.paper',
                    boxShadow: 'none',
                  },
                },
              }}
              value={query}
            />
          </Box>
        )}
        <Scrollbar
          style={{
            maxHeight: '60vh',
            width: '100%',
          }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <MenuItem
                key={option.value}
                ref={index === 0 ? firstMenuItemRef : null}
                disabled={option.disabled}
                onClick={() => handleOptionClick(option)}
                selected={option.value === value}
                sx={{
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  whiteSpace: 'normal',
                }}
                value={option.value}>
                {option.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{noOptionsText ?? t('common:noResults')}</MenuItem>
          )}
        </Scrollbar>
      </Menu>
    </>
  );
}
